/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

import { useAuth } from '@/providers/auth';

import { applyConsent, ensureConsentChangeListener, reconcileDntCache } from './apply-consent';

/**
 * Turns OneTrust's consent callbacks into action. Renders nothing.
 *
 * The OneTrust SDK loads from `<head>` long before React hydrates, and calls `window.OptanonWrapper`
 * as soon as it knows the shopper's choices. `root.tsx` installs a shim there that forwards to
 * `window.__otHandler` and queues the callback in `window.__otQueue` if nothing is listening yet;
 * this component installs that handler and drains the queue — the same queue-then-replay shape
 * `window.adobeDataLayer` uses for early page views.
 *
 * Consent changes arrive two ways, and both are wired because neither is sufficient alone:
 * `OptanonWrapper` re-fires when the preference centre is saved, and `OnConsentChanged` covers the
 * banner buttons. Registration is deferred to the first callback because the SDK has not installed
 * `window.OneTrust` yet at mount time.
 *
 * Note this deliberately does NOT touch `buildConsentPreferences()` or `useTrackingConsent()`.
 * OneTrust is the single source of truth for consent here; two systems driving the same vendor's
 * enable/disable API would fight. See docs/README-CONSENT-TRACKING.md.
 */
export default function OneTrustConsentBridge(): null {
    const config = useConfig();
    const adobeLaunchSrc = config?.adobeLaunch?.src ?? '';
    // The server's own view of the DNT signal, shipped by the root loader. Used only to keep the
    // client's post-dedupe cache honest — see reconcileDntCache.
    const serverTrackingConsent = useAuth()?.trackingConsent;

    useEffect(() => {
        // Before anything reads the cache, make sure it still agrees with the server.
        reconcileDntCache(serverTrackingConsent);

        const handler = () => {
            applyConsent(adobeLaunchSrc);
            // Deferred to the first callback because the SDK has not installed `window.OneTrust`
            // yet at mount time. Guarded at module scope inside the helper — see the note there.
            ensureConsentChangeListener(adobeLaunchSrc);
        };

        window.__otHandler = handler;

        // Drain callbacks the `<head>` shim queued before this mounted. Anything in the queue means
        // the SDK has already reported consent, so one replay is enough regardless of the count.
        if (window.__otQueue?.length) {
            window.__otQueue = [];
            handler();
        } else if (window.OnetrustActiveGroups) {
            // The SDK loaded and ran its callback before the shim existed (possible on a client-side
            // navigation into a freshly hydrated tree). The globals are already populated, so apply
            // them rather than waiting for a change that may never come.
            handler();
        }

        return () => {
            if (window.__otHandler === handler) {
                delete window.__otHandler;
            }
        };
    }, [adobeLaunchSrc, serverTrackingConsent]);

    return null;
}
